import { ChannelPageType, Institution, Plan } from '@faceup/utils'
import { useContext } from 'react'
import { UiContext } from '../UiProvider/UiProvider'

export const pagesTranslationVariants = [
  'freeSchool',
  'testingSchool',
  'unregisteredSchool',
  'school',
  'company',
  'surveyBeforeSend',
  'formItems',
  'surveyAfterSend',
] as const

export type PagesTranslationVariants = (typeof pagesTranslationVariants)[number]

export type PagesTranslationItems = 'title' | 'content'

type TypeUnion =
  | { __typename: 'Page'; type?: never }
  | { __typename: 'ChannelPage'; type: ChannelPageType }

type PageTemplate<D extends object = object> = D &
  Partial<Record<'title' | 'content', string | null>> &
  TypeUnion

type ExtractChannelPage<T> = T extends { __typename: 'ChannelPage' } ? T : never
type ChannelPageOnly = ExtractChannelPage<TypeUnion>

export type PageTranslations<D extends object = object> = D &
  Record<PagesTranslationItems, string | undefined>
const usePageTemplate = (
  forceTranslations?: Record<PagesTranslationVariants, Record<PagesTranslationItems, string>>
) => {
  const { pages } = useContext(UiContext)
  const getDefaultTranslations = (): PageTranslations => {
    if (!pages) {
      return {
        title: '',
        content: '',
      }
    }

    const { mother, isDefaultForm } = pages
    const translations = forceTranslations ?? pages.translations

    const { type, isTesting, plan } = mother

    if (type === Institution.School && isDefaultForm) {
      if (isTesting) {
        return {
          title: translations.testingSchool.title,
          content: translations.testingSchool.content,
        }
      }

      if (plan === Plan.Unregistered) {
        return {
          title: translations.unregisteredSchool.title,
          content: translations.unregisteredSchool.content,
        }
      }

      if (plan === Plan.Free) {
        return {
          title: translations.freeSchool.title,
          content: translations.freeSchool.content,
        }
      }

      return {
        title: translations.school.title,
        content: translations.school.content,
      }
    }

    return {
      title: translations.company.title,
      content: translations.company.content,
    }
  }

  const getDefaultSurveyPageTranslations = <D extends object>(
    page: PageTemplate<D>
  ): PageTranslations<D> => {
    if (page.__typename !== 'ChannelPage') {
      throw Error("Can't get survey translations for non ChannelPage")
    }
    if (!pages) {
      return {
        ...page,
        title: '',
        content: '',
      }
    }
    const translations = forceTranslations ?? pages.translations
    const returnObjects: Record<ChannelPageType, () => PageTranslations<D>> = {
      [ChannelPageType.BeforeSend]: () => ({
        ...page,
        title: translations?.surveyBeforeSend.title,
        content: translations?.surveyBeforeSend.content,
      }),
      [ChannelPageType.FormItems]: () => ({
        ...page,
        title: translations?.formItems.title,
        content: translations?.formItems.content,
      }),
      [ChannelPageType.AfterSend]: () => ({
        ...page,
        title: translations?.surveyAfterSend.title,
        content: translations?.surveyAfterSend.content,
      }),
    }

    return returnObjects[page.type]()
  }

  return {
    fillObject: <D extends object>(page: PageTemplate<D>): PageTranslations<D> => {
      if (page.__typename === 'ChannelPage') {
        const defaultSurveyTranslations = getDefaultSurveyPageTranslations(page)
        return {
          ...page,
          // if undefined, probably not returned from gql, so left as is
          title:
            page.title === undefined ? undefined : (page.title ?? defaultSurveyTranslations?.title),
          content:
            page.content === undefined
              ? undefined
              : (page.content ?? defaultSurveyTranslations?.content),
        }
      }
      const defaultValues = getDefaultTranslations()
      return {
        ...page,
        // if undefined, probably not returned from gql, so left as is
        title: page.title === undefined ? undefined : (page.title ?? defaultValues.title),
        content: page.content === undefined ? undefined : (page.content ?? defaultValues.content),
      }
    },
    isTitleDefault: (title: string) => {
      const { title: defaultTitle } = getDefaultTranslations()
      return title === defaultTitle
    },
    isContentDefault: (content: string) => {
      const { content: defaultContent } = getDefaultTranslations()
      return content === defaultContent
    },
    isSurveyContentDefault: (content: string, page: ChannelPageOnly) => {
      const { content: defaultContent } = getDefaultSurveyPageTranslations(page)
      return content === defaultContent
    },
    isSurveyTitleDefault: (title: string, page: ChannelPageOnly) => {
      const { title: defaultTitle } = getDefaultSurveyPageTranslations(page)
      return title === defaultTitle
    },
    setIsDefaultForm: pages?.setIsDefaultForm ?? (() => undefined),
  }
}

export default usePageTemplate
